(() => {
  const config = document.currentScript?.dataset || {};
  const widgetSelector = '.jdgm-widget';
  const observedWidgets = new WeakSet();

  const addReviewTabs = (widget) => {
    if (!widget.matches('.jdgm-review-widget') || widget.closest('[data-inkybox-review-tabs]') ||
        !config.storeReviewsModule || !config.storeReviewsEndpoint) return;
    const wrapper = document.createElement('div');
    wrapper.className = 'inkybox-review-tabs';
    wrapper.dataset.inkyboxReviewTabs = '';
    const tabs = document.createElement('div');
    tabs.className = 'inkybox-review-tabs__nav';
    tabs.setAttribute('role', 'tablist');
    tabs.setAttribute('aria-label', 'Customer reviews');
    const panels = ['item', 'store'].map((type, index) => {
      const panel = document.createElement('div');
      panel.id = `${widget.id || 'judgeme'}-inkybox-${type}-panel`;
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', `${panel.id}-tab`);
      panel.hidden = index !== 0;
      panel.tabIndex = 0;
      const tab = document.createElement('button');
      tab.type = 'button';
      tab.id = `${panel.id}-tab`;
      tab.textContent = index ? 'Store Reviews' : 'Item Reviews';
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', panel.id);
      tab.setAttribute('aria-selected', String(index === 0));
      tab.tabIndex = index ? -1 : 0;
      tabs.append(tab);
      return panel;
    });
    widget.before(wrapper);
    wrapper.append(tabs, ...panels);
    panels[0].append(widget);
    let layoutModule;
    const getLayout = () => layoutModule ||= import(config.storeReviewsModule).catch((error) => {
      layoutModule = null;
      throw error;
    });
    if (widget.dataset.productId || widget.dataset.id) {
      const observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        getLayout().then((module) => module.mountItemReviews(panels[0], widget, config.storeReviewsEndpoint)).catch(() => {});
      }, { rootMargin: '200px' });
      observer.observe(wrapper);
    }
    let loaded = false;
    let refreshStore;
    let importing;
    const select = async (index) => {
      [...tabs.children].forEach((tab, i) => {
        tab.setAttribute('aria-selected', String(i === index));
        tab.tabIndex = i === index ? 0 : -1;
        panels[i].hidden = i !== index;
      });
      if (!index) return;
      if (loaded) { refreshStore(); return; }
      if (importing) return;
      panels[1].textContent = 'Loading store reviews…';
      importing = getLayout();
      try {
        const module = await importing;
        refreshStore = module.mountStoreReviews(panels[1], config.storeReviewsEndpoint);
        loaded = true;
      } catch {
        panels[1].replaceChildren();
        const retry = document.createElement('button');
        retry.type = 'button';
        retry.textContent = 'Reviews could not load. Try again';
        retry.addEventListener('click', () => select(1), { once: true });
        panels[1].append(retry);
      } finally { importing = null; }
    };
    tabs.addEventListener('click', (event) => {
      const index = [...tabs.children].indexOf(event.target.closest('button'));
      if (index >= 0) select(index);
    });
    tabs.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const current = [...tabs.children].indexOf(document.activeElement);
      const index = event.key === 'Home' ? 0 : event.key === 'End' ? 1 : 1 - Math.max(0, current);
      select(index);
      tabs.children[index].focus();
    });
    // The existing product-rating badge must always reveal its native review widget.
    document.addEventListener('click', (event) => {
      if (event.target.closest('.jdgm-preview-badge, a[href="#judgeme_product_reviews"]')) select(0);
    }, true);
  };

  const updateReviewState = (widget) => {
    widget.classList.toggle('inkybox-review-has-reviews', Boolean(widget.querySelector('.jdgm-rev')));
  };

  const enhanceWidget = (widget) => {
    addReviewTabs(widget);
    if (observedWidgets.has(widget)) return;
    let frame = 0;
    const scheduleBuild = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        updateReviewState(widget);
      });
    };

    scheduleBuild();

    observedWidgets.add(widget);
    new MutationObserver(scheduleBuild).observe(widget, { childList: true, subtree: true });
  };

  const findWidgets = () => document.querySelectorAll(widgetSelector).forEach(enhanceWidget);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', findWidgets, { once: true });
  } else {
    findWidgets();
  }

  const mountObserver = new MutationObserver(() => {
    findWidgets();
    if (document.querySelector(widgetSelector)) mountObserver.disconnect();
  });

  mountObserver.observe(document.documentElement, { childList: true, subtree: true });
  window.setTimeout(() => mountObserver.disconnect(), 15000);
  document.addEventListener('shopify:section:load', findWidgets);
})();
